import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Row} from 'react-table';

import {ELoading} from '@ELib/components/ETable/ELoading';

import {getColSpan, TBodyRow} from './TBodyRow';
import {useActionsHandlerForSubRow} from './hooks';
import {
  ETableActiveItem,
  ETableRow,
  ETableRowActionHandler,
  ETableRowClickHandler,
  ETableRowExpansion,
  ETableSubRow,
  isCellObject,
} from './types';

export const TBodySubRows = ({
  activeItem,
  onRowClick,
  parent: parentRow,
}: {
  activeItem: ETableActiveItem | undefined;
  onRowClick: ETableRowClickHandler;
  parent: Row<ETableRow>;
}): React.ReactElement | null => {
  const [isLoading, setIsLoading] = useState(false);
  const [subRows, setSubRows] = useState<ETableRowExpansion>();

  /** Ref used to avoid unnecessary rerenderings of subrows. */
  const parentRowRef = useRef<Row<ETableRow>>();
  parentRowRef.current = parentRow;

  const parentRowExpansion = parentRowRef.current!.original.options?.expansion;

  // load potentially async sub row(s)
  useEffect(() => {
    const getSubRowsData = async () => {
      setIsLoading(true);
      const subRowsResult =
        await parentRowRef.current!.original.options?.expansion?.(
          parentRowRef.current!.original
        );
      setIsLoading(false);
      setSubRows(subRowsResult);
    };
    getSubRowsData();
  }, [parentRowRef, parentRowExpansion]);

  const hasSelectionColumn = parentRow.cells[0]?.column.id === '_selection';
  /** Colspan of main content cell (i.e. without selection and expansion). */
  const mainCellColSpan =
    parentRow.cells.length - (hasSelectionColumn ? 1 : 0) - 1;

  const actionsHandler: ETableRowActionHandler =
    useActionsHandlerForSubRow(subRows);

  const {t} = useTranslation('lib');

  if (isLoading) {
    return (
      <tr className='noHover'>
        {hasSelectionColumn && <td className='selection' />}
        <td className='expansion' />
        <td colSpan={mainCellColSpan}>
          <ELoading noDelay />
        </td>
      </tr>
    );
  }

  if (!subRows) {
    // avoid flashing of empty content while the useEffect is still running
    return null;
  }

  if (Array.isArray(subRows) && subRows.length === 0) {
    return (
      <tr>
        {hasSelectionColumn && <td className='selection' />}
        <td className='expansion' />
        <td colSpan={mainCellColSpan} style={{textAlign: 'center'}}>
          {t('placeholder.noContent')}
        </td>
      </tr>
    );
  }

  // one or more additional rows
  if (Array.isArray(subRows)) {
    const rowProps = parentRow.getRowProps();
    return (
      <>
        {subRows.map((subRow, subRowIndex) => {
          const subRowWithParent = {...parentRow, original: subRow};
          return (
            <TBodyRow
              // eslint-disable-next-line react/no-array-index-key
              key={`${rowProps.key}-expanded-${subRowIndex}`}
              actions={actionsHandler}
              activeItem={activeItem}
              isDraggable={false}
              parentRow={parentRow}
              row={subRowWithParent}
              subRowIndex={subRowIndex}
              onRowClick={onRowClick}
            />
          );
        })}
      </>
    );
  }

  // there is only a single row available; rename to make it readable
  const subRow = subRows;

  // sub element (mostly a table) that spans the whole width
  if (React.isValidElement(subRow)) {
    return (
      <tr className='noHover'>
        {hasSelectionColumn && <td className='selection' />}
        <td className='expansion' />
        <td className='subContainer' colSpan={mainCellColSpan}>
          {subRow}
        </td>
      </tr>
    );
  }

  // at this point we can only assume that the subRow is a ETableSubRow<string>
  // but typescript does not understand the typeguard in isValidElement
  // problem described here: https://github.com/microsoft/TypeScript/issues/53178
  const tableSubRow = subRow as ETableSubRow<string>;

  /**
   * Contains a list of *ordered* column values. The sub row's object properties may not
   * be ordered according to the actual column order. But to calculate the colspan, the
   * order is essential.
   */
  const orderedCellsWithValue = parentRow.cells.map((cell) => ({
    id: cell.column.id,
    value: tableSubRow[cell.column.id],
  }));

  // sub element (mostly a table) that only spans certain columns
  return (
    <tr className='noHover'>
      {parentRow.cells.map((cell, index) => {
        const key = cell.column.id;
        if (cell.column.id === '_selection') {
          return <td key={key} className='selection' />;
        }
        if (cell.column.id === '_expander') {
          return <td key={key} className='expansion' />;
        }
        const subCell = tableSubRow[cell.column.id];
        const rawCellValue = isCellObject(subCell) ? subCell.cell : subCell;
        if (rawCellValue === null) {
          // used with colspan
          return null;
        }
        if (rawCellValue === '' || rawCellValue === undefined) {
          return <td key={key} />;
        }
        return (
          <td
            key={key}
            className='subContainer'
            colSpan={getColSpan(orderedCellsWithValue, index)}
          >
            {rawCellValue as React.ReactNode}
          </td>
        );
      })}
    </tr>
  );
};
